'use client';

import React, { useState, useEffect } from 'react';
import CustomerNavigation from '@/components/common/CustomerNavigation';
import HeroVideoSlider from './HeroVideoSlider';
import ProductCard from './ProductCard';
import SearchBar from './SearchBar';

import VariantSelectorModal from './VariantSelectorModal';

import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';
import SimpleContentModal from '@/components/common/SimpleContentModal';

interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  rating: number;
  reviewCount: number;
  image: string;
  alt: string;
  brand: string;
  skinTypes: string[];
  inStock: boolean;
  hasVariants: boolean;
}

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
}

const ProductCatalogInteractive = () => {
  const { t, language } = useLanguage();
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedSkinTypes, setSelectedSkinTypes] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 200]);
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isIframeModalOpen, setIsIframeModalOpen] = useState(false);
  const [catalogKey, setCatalogKey] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Force re-render when language changes to update product names/descriptions
  useEffect(() => {
    setCatalogKey((prev) => prev + 1);
  }, [language, t]);

  const mockProducts: Product[] = [
  {
    id: '1',
    name: t('products.arganOil.name'),
    price: 180.00,
    rating: 4.9,
    reviewCount: 245,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_136fd03b6-1769752800072.png",
    alt: 'Celine Claire Moroccan Argan Oil 100ml - Liquid gold enriched with fatty acids, oleic acid, linoleic acid, and vitamin E for hair moisturizing, scalp health, and styling protection',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '2',
    name: t('products.hairMask.name'),
    price: 217.00,
    rating: 4.8,
    reviewCount: 312,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17b6871e1-1765201172676.png",
    alt: 'Celine Claire Intense Hair Mask 400ml jar for deep conditioning treatment',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '3',
    name: t('products.purpleRepairMask.name'),
    price: 217.00,
    rating: 4.7,
    reviewCount: 189,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f967564d-1769517155363.png",
    alt: 'Celine Claire Purple Repair Mask 400ml - Ultra-pigmented deep purple toning formula with argan oil for blonde hair brassiness removal, treats UV exposure damage, hard water damage, chlorine damage, repairs over-processed hair, neutralizes orange and copper tones, salon alternative for blonde and color-treated hair',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '4',
    name: t('products.hairEnhancementShampoo.name'),
    price: 158.00,
    rating: 4.6,
    reviewCount: 278,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18fad6241-1769517154073.png",
    alt: 'Celine Claire Hair Enhancement Shampoo 300ml pump bottle for strengthening hair',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '5',
    name: t('products.volumiserShampoo.name'),
    price: 130.00,
    rating: 4.5,
    reviewCount: 156,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1428de8a8-1769517154247.png",
    alt: 'Celine Claire Volumiser Shampoo 500ml for adding volume and body to hair',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '6',
    name: t('products.volumiserConditioner.name'),
    price: 130.00,
    rating: 4.5,
    reviewCount: 143,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13530885a-1769517154986.png",
    alt: 'Celine Claire Volumiser Conditioner 500ml for lightweight conditioning and volume',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '7',
    name: t('products.hydratingShampoo.name'),
    price: 130.00,
    rating: 4.7,
    reviewCount: 234,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1345a870a-1769517153593.png",
    alt: 'Celine Claire Hydrating Shampoo 500ml with antioxidant-rich argan oil for moisture-rich cleansing',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '8',
    name: t('products.hydratingConditioner.name'),
    price: 130.00,
    rating: 4.6,
    reviewCount: 198,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15684460c-1769517152756.png",
    alt: 'Celine Claire Hydrating Conditioner 500ml - Intensely moisturizing conditioner with argan oil, cationic surfactants, silicones, and vitamin E for frizz control, damage repair, and scalp health',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '9',
    name: t('products.cck1And2.name'),
    price: 1285.00,
    rating: 4.9,
    reviewCount: 87,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b01c010c-1769517155912.png",
    alt: 'Celine Claire CCK1 and CCK2 Keratin Treatment Set 2x500ml for professional smoothing',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '10',
    name: t('products.cck3.name'),
    price: 158.00,
    rating: 4.8,
    reviewCount: 167,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15194c499-1769517154132.png",
    alt: 'Celine Claire CCK3 Daily Keratin Shampoo 500ml for maintaining keratin treatments',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '11',
    name: t('products.cck4.name'),
    price: 158.00,
    rating: 4.8,
    reviewCount: 154,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_187c16c76-1765777880816.png",
    alt: 'Celine Claire CCK4 Daily Keratin Conditioner 500ml for keratin-treated hair care',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '12',
    name: t('products.aquaStylingWax.name'),
    price: 130.00,
    rating: 4.6,
    reviewCount: 142,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_141a2c812-1769517339525.png",
    alt: 'Celine Claire Aqua Styling Hair Wax 200ml for flexible hold and texture',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '13',
    name: t('products.deadSeaSaltSpray.name'),
    price: 85.00,
    rating: 4.5,
    reviewCount: 128,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10456bf72-1769517343943.png",
    alt: 'Celine Claire Dead Sea Salt Spray 200ml for beachy waves and volume',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '14',
    name: t('products.heatShieldSpray.name'),
    price: 92.00,
    rating: 4.7,
    reviewCount: 195,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dd4a504b-1769517340213.png",
    alt: 'Celine Claire Heat Shield Spray 100ml for heat protection up to 230°C',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '15',
    name: t('products.moisturizingCream.name'),
    price: 129.00,
    rating: 4.8,
    reviewCount: 176,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef99c919-1769517341425.png",
    alt: 'Celine Claire Moisturizing Cream Leave-in Conditioner 200ml for all-day hydration',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '16',
    name: t('products.curlEssence.name'),
    price: 129.00,
    rating: 4.7,
    reviewCount: 163,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17efab05b-1769517338680.png",
    alt: 'Celine Claire Curl Essence Leave-in Conditioner 200ml for defined curls',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '17',
    name: t('products.tecplexKits.name'),
    price: 327.00,
    rating: 4.9,
    reviewCount: 94,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11723123a-1769517345899.png",
    alt: 'Celine Claire TecPlex Kits Professional 3x100ml for bond repair treatment',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '18',
    name: t('products.tecplexNo3.name'),
    price: 92.00,
    rating: 4.8,
    reviewCount: 187,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12221cbc3-1769517341037.png",
    alt: 'Celine Claire TecPlex No.3 100ml hair perfector for at-home bond building',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '19',
    name: t('products.tecplexNo7.name'),
    price: 144.00,
    rating: 4.8,
    reviewCount: 152,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1393e027c-1769517339376.png",
    alt: 'Celine Claire TecPlex No7 Oil 90ml bonding oil for shine and repair',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '20',
    name: t('products.silverShampoo.name'),
    price: 110.00,
    rating: 4.7,
    reviewCount: 201,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17032c5a3-1769517340071.png",
    alt: 'Celine Claire Silver Shampoo 300ml - Purple shampoo with ultra-violet pigments for color theory-based brass neutralization, transforms yellow blonde tones, vegan cruelty-free gluten-free formula, salon-strength micro-pigments for platinum silver hair',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '21',
    name: t('products.flexyBrush.name'),
    price: 92.00,
    rating: 4.6,
    reviewCount: 118,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e7158e1a-1769517339390.png",
    alt: 'Celine Claire Flexy Single Hair Brush for gentle detangling and styling',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  },
  {
    id: '22',
    name: t('products.flexyBrushSet.name'),
    price: 165.00,
    rating: 4.8,
    reviewCount: 89,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8a61c7b-1769517344514.png",
    alt: 'Celine Claire Professional Flexy Brush Set 4 pieces for complete hair care',
    brand: 'Celine Claire',
    skinTypes: ['All'],
    inStock: true,
    hasVariants: false
  }];


  const filterProducts = () => {
    let filtered = [...mockProducts];

    if (selectedBrands.length > 0) {
      filtered = filtered.filter((p) => selectedBrands.includes(p.brand));
    }

    if (selectedSkinTypes.length > 0) {
      filtered = filtered.filter((p) =>
      p.skinTypes.some((type) => selectedSkinTypes.includes(type) || type === 'All')
      );
    }

    filtered = filtered.filter((p) => p.price >= priceRange[0] && p.price <= priceRange[1]);

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
        p.name.toLowerCase().includes(query) ||
        p.brand.toLowerCase().includes(query)
      );
    }

    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        filtered.reverse();
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredProducts = isHydrated ? filterProducts() : mockProducts;

  const handleAddToCart = (productId: string) => {
    const product = mockProducts.find((p) => p.id === productId);
    if (!product) return;

    if (product.hasVariants) {
      setSelectedProduct(product);
    } else {
      addToCart(productId);
    }
  };

  const addToCart = (productId: string, variantId?: string) => {
    const product = mockProducts.find((p) => p.id === productId);
    if (!product) return;

    setCartItems((prev) => {
      const existingItem = prev.find((item) => item.id === productId);
      if (existingItem) {
        return prev.map((item) =>
        item.id === productId ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [
      ...prev,
      {
        id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        image: product.image
      }];

    });

    setSelectedProduct(null);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const handleBrandToggle = (brand: string) => {
    setSelectedBrands((prev) =>
    prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  const handleSkinTypeToggle = (skinType: string) => {
    setSelectedSkinTypes((prev) =>
    prev.includes(skinType) ? prev.filter((s) => s !== skinType) : [...prev, skinType]
    );
  };

  const handleClearFilters = () => {
    setSelectedBrands([]);
    setSelectedSkinTypes([]);
    setPriceRange([0, 200]);
    setSearchQuery('');
  };

  const cartItemCount = isHydrated ? cartItems.reduce((sum, item) => sum + item.quantity, 0) : 0;
  const cartTotal = isHydrated ? cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0) : 0;

  if (!isHydrated) {
    return null;
  }

  return (
    <>
      <CustomerNavigation data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:469:6" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="469" data-component-end-line="469" data-component-file="ProductCatalogInteractive.tsx" data-component-name="CustomerNavigation" data-component-content="%7B%22elementName%22%3A%22CustomerNavigation%22%7D" cartItemCount={cartItemCount} isAuthenticated={false} />

      <HeroVideoSlider data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:471:6" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="471" data-component-end-line="471" data-component-file="ProductCatalogInteractive.tsx" data-component-name="HeroVideoSlider" data-component-content="%7B%22elementName%22%3A%22HeroVideoSlider%22%7D" />
      
      {/* Brand Title and Slogan Section */}
      <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:474:6" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="474" data-component-end-line="486" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20bg-black%20py-8%20sm%3Apy-12%20lg%3Apy-16%22%7D" className="w-full bg-black py-8 sm:py-12 lg:py-16">
        <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:475:8" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="475" data-component-end-line="485" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20max-w-7xl%20px-4%20text-center%22%7D" className="mx-auto max-w-7xl px-4 text-center">
          <h1 data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:476:10" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="476" data-component-end-line="478" data-component-file="ProductCatalogInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22mb-2%20sm%3Amb-3%20text-4xl%20sm%3Atext-5xl%20lg%3Atext-6xl%20font-extralight%20tracking-tight%20text-white%20uppercase%22%7D" className="mb-2 sm:mb-3 text-4xl sm:text-5xl lg:text-6xl font-extralight tracking-tight text-white uppercase" style={{ fontFamily: '"Segoe UI Light", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif' }}>
            {t('catalog.brandName')}
          </h1>
          <p data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:479:10" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="479" data-component-end-line="481" data-component-file="ProductCatalogInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mb-4%20sm%3Amb-6%20font-sans%20text-base%20sm%3Atext-lg%20lg%3Atext-xl%20font-light%20tracking-wide%20text-%5B%23f2a936%5D%22%7D" className="mb-4 sm:mb-6 font-sans text-base sm:text-lg lg:text-xl font-light tracking-wide text-[#f2a936]">
            {t('catalog.tagline')}
          </p>
          <h2 data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:482:10" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="482" data-component-end-line="484" data-component-file="ProductCatalogInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-sans%20text-3xl%20sm%3Atext-4xl%20lg%3Atext-5xl%20font-extralight%20tracking-tight%20text-white%20uppercase%22%7D" className="font-sans text-3xl sm:text-4xl lg:text-5xl font-extralight tracking-tight text-white uppercase">
            {t('catalog.callToAction')}
          </h2>
        </div>
      </div>

      {/* Three Large Action Buttons */}
      <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:489:6" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="489" data-component-end-line="519" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20bg-black%20pt-12%20sm%3Apt-16%20lg%3Apt-20%20pb-2%20sm%3Apb-3%20lg%3Apb-4%22%7D" className="w-full bg-black pt-12 sm:pt-16 lg:pt-20 pb-2 sm:pb-3 lg:pb-4">
        <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:490:8" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="490" data-component-end-line="518" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20max-w-7xl%20px-4%22%7D" className="mx-auto max-w-7xl px-4">
          <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:491:10" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="491" data-component-end-line="517" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20gap-4%20sm%3Agap-6%20md%3Agrid-cols-3%22%7D" className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-3">
            <button data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:492:12" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="492" data-component-end-line="499" data-component-file="ProductCatalogInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22h-32%20sm%3Ah-36%20lg%3Ah-40%20w-full%20bg-%5B%23f2a936%5D%20hover%3Abg-%5B%23e09825%5D%20transition-all%20duration-300%20rounded-sm%20shadow-lg%20hover%3Ashadow-xl%20transform%20hover%3A-translate-y-1%20flex%20flex-col%20items-center%20justify-center%20px-4%22%7D"
              className="h-32 sm:h-36 lg:h-40 w-full bg-[#f2a936] hover:bg-[#e09825] transition-all duration-300 rounded-sm shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex flex-col items-center justify-center px-4"
              aria-label={t('catalog.uniqueTitle')}>

              <span data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:496:14" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="496" data-component-end-line="496" data-component-file="ProductCatalogInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-black%20text-lg%20sm%3Atext-xl%20lg%3Atext-2xl%20font-bold%20mb-1%22%7D" className="text-black text-lg sm:text-xl lg:text-2xl font-bold mb-1">{t('catalog.uniqueTitle')}</span>
              <span data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:497:14" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="497" data-component-end-line="497" data-component-file="ProductCatalogInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-white%20text-sm%20sm%3Atext-base%20font-light%20mb-1%22%7D" className="text-white text-sm sm:text-base font-light mb-1">{t('catalog.uniqueSubtitle')}</span>
              <span data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:498:14" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="498" data-component-end-line="498" data-component-file="ProductCatalogInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-black%20text-xs%20sm%3Atext-sm%20font-medium%20uppercase%20tracking-wide%22%7D" className="text-black text-xs sm:text-sm font-medium uppercase tracking-wide">{t('catalog.uniqueAction')}</span>
            </button>
            <button data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:500:12" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="500" data-component-end-line="508" data-component-file="ProductCatalogInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22h-32%20sm%3Ah-36%20lg%3Ah-40%20w-full%20bg-%5B%23f2a936%5D%20hover%3Abg-%5B%23e09825%5D%20transition-all%20duration-300%20rounded-sm%20shadow-lg%20hover%3Ashadow-xl%20transform%20hover%3A-translate-y-1%20flex%20flex-col%20items-center%20justify-center%20px-4%22%7D"
              className="h-32 sm:h-36 lg:h-40 w-full bg-[#f2a936] hover:bg-[#e09825] transition-all duration-300 rounded-sm shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex flex-col items-center justify-center px-4"
              aria-label={t('catalog.expertTitle')}
              onClick={() => setIsIframeModalOpen(true)}>

              <span data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:505:14" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="505" data-component-end-line="505" data-component-file="ProductCatalogInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-black%20text-lg%20sm%3Atext-xl%20lg%3Atext-2xl%20font-bold%20mb-1%22%7D" className="text-black text-lg sm:text-xl lg:text-2xl font-bold mb-1">{t('catalog.expertTitle')}</span>
              <span data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:506:14" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="506" data-component-end-line="506" data-component-file="ProductCatalogInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-white%20text-sm%20sm%3Atext-base%20font-light%20mb-1%22%7D" className="text-white text-sm sm:text-base font-light mb-1">{t('catalog.expertSubtitle')}</span>
              <span data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:507:14" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="507" data-component-end-line="507" data-component-file="ProductCatalogInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-black%20text-xs%20sm%3Atext-sm%20font-medium%20uppercase%20tracking-wide%22%7D" className="text-black text-xs sm:text-sm font-medium uppercase tracking-wide">{t('catalog.expertAction')}</span>
            </button>
            <button data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:509:12" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="509" data-component-end-line="516" data-component-file="ProductCatalogInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22h-32%20sm%3Ah-36%20lg%3Ah-40%20w-full%20bg-%5B%23f2a936%5D%20hover%3Abg-%5B%23e09825%5D%20transition-all%20duration-300%20rounded-sm%20shadow-lg%20hover%3Ashadow-xl%20transform%20hover%3A-translate-y-1%20flex%20flex-col%20items-center%20justify-center%20px-4%22%7D"
              className="h-32 sm:h-36 lg:h-40 w-full bg-[#f2a936] hover:bg-[#e09825] transition-all duration-300 rounded-sm shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex flex-col items-center justify-center px-4"
              aria-label={t('catalog.caresTitle')}>

              <span data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:513:14" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="513" data-component-end-line="513" data-component-file="ProductCatalogInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-black%20text-lg%20sm%3Atext-xl%20lg%3Atext-2xl%20font-bold%20mb-1%22%7D" className="text-black text-lg sm:text-xl lg:text-2xl font-bold mb-1">{t('catalog.caresTitle')}</span>
              <span data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:514:14" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="514" data-component-end-line="514" data-component-file="ProductCatalogInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-white%20text-sm%20sm%3Atext-base%20font-light%20mb-1%22%7D" className="text-white text-sm sm:text-base font-light mb-1">{t('catalog.caresSubtitle')}</span>
              <span data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:515:14" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="515" data-component-end-line="515" data-component-file="ProductCatalogInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-black%20text-xs%20sm%3Atext-sm%20font-medium%20uppercase%20tracking-wide%22%7D" className="text-black text-xs sm:text-sm font-medium uppercase tracking-wide">{t('catalog.caresAction')}</span>
            </button>
          </div>
        </div>
      </div>

      <main data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:521:6" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="521" data-component-end-line="599" data-component-file="ProductCatalogInteractive.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pb-12%20pt-12%20sm%3Apt-16%20lg%3Apt-20%22%7D" className="min-h-screen bg-background pb-12 pt-12 sm:pt-16 lg:pt-20">
        <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:522:8" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="522" data-component-end-line="598" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20max-w-7xl%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:523:10" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="523" data-component-end-line="525" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
            <SearchBar data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:524:12" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="524" data-component-end-line="524" data-component-file="ProductCatalogInteractive.tsx" data-component-name="SearchBar" data-component-content="%7B%22elementName%22%3A%22SearchBar%22%7D" onSearch={setSearchQuery} />
          </div>

          {/* Full-width Dubai Banner with 2 columns */}
          <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:528:10" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="528" data-component-end-line="539" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22-mx-4%20sm%3A-mx-6%20lg%3A-mx-8%20mb-6%20-mt-4%22%7D" className="-mx-4 sm:-mx-6 lg:-mx-8 mb-6 -mt-4">
            <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:529:12" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="529" data-component-end-line="538" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20flex%22%7D" className="w-full flex" style={{ backgroundColor: '#2d2530' }}>
              {/* Second column with Dubai promotional banner */}
              <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:531:14" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="531" data-component-end-line="537" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
                <img data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:532:16" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="532" data-component-end-line="535" data-component-file="ProductCatalogInteractive.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fassets%2Fimages%2FCELINE_HABIBI-1769772054094.png%22%2C%22alt%22%3A%22Celine%20Claire%20-%20Now%20also%20in%20Dubai!%20Habibi%20you%20gotta%20love%20celine!%22%2C%22className%22%3A%22w-full%20h-auto%20object-cover%22%7D"
                  src="/assets/images/CELINE_HABIBI-1769772054094.png"
                  alt="Celine Claire - Now also in Dubai! Habibi you gotta love celine!"
                  className="w-full h-auto object-cover" />

              </div>
            </div>
          </div>

          <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:541:10" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="541" data-component-end-line="562" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%20flex%20flex-row%20items-center%20justify-end%20gap-2%20md%3Agap-4%22%7D" className="mb-6 flex flex-row items-center justify-end gap-2 md:gap-4">
            <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:542:12" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="542" data-component-end-line="561" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20flex-shrink-0%22%7D" className="flex items-center gap-2 flex-shrink-0">
              <button data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:543:14" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="543" data-component-end-line="551" data-component-file="ProductCatalogInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                onClick={() => setViewMode('grid')}
                className={`flex items-center justify-center p-2 transition-smooth outline-none border-0 flex-shrink-0 ${
                viewMode === 'grid' ? 'bg-white text-black' : 'bg-[#f2a936] text-black hover:bg-white'}`
                }
                aria-label={t('catalog.gridView')}>

                <Icon data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:550:16" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="550" data-component-end-line="550" data-component-file="ProductCatalogInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22Squares2X2Icon%22%7D" name="Squares2X2Icon" size={20} />
              </button>
              <button data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:552:14" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="552" data-component-end-line="560" data-component-file="ProductCatalogInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                onClick={() => setViewMode('list')}
                className={`flex items-center justify-center p-2 transition-smooth outline-none border-0 flex-shrink-0 ${
                viewMode === 'list' ? 'bg-white text-black' : 'bg-[#f2a936] text-black hover:bg-white'}`
                }
                aria-label={t('catalog.listView')}>

                <Icon data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:559:16" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="559" data-component-end-line="559" data-component-file="ProductCatalogInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22Bars3Icon%22%7D" name="Bars3Icon" size={20} />
              </button>
            </div>
          </div>

          <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:564:10" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="564" data-component-end-line="597" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            {filteredProducts.length > 0 ?
            <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:566:12" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="566" data-component-end-line="580" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              key={`catalog-${catalogKey}-${Date.now()}`}
              className={`${
              viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' : 'flex flex-col gap-4'}`
              }>

                {filteredProducts.map((product) =>
              <ProductCard data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:573:14" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="573" data-component-end-line="577" data-component-file="ProductCatalogInteractive.tsx" data-component-name="ProductCard" data-component-content="%7B%22elementName%22%3A%22ProductCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                key={`${product.id}-${catalogKey}-${Date.now()}`}
                {...product}
                onAddToCart={handleAddToCart}
                viewMode={viewMode} />

              )}
              </div> :

            <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:582:12" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="582" data-component-end-line="595" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20justify-center%20py-16%22%7D" className="flex flex-col items-center justify-center py-16">
                <Icon data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:583:16" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="583" data-component-end-line="583" data-component-file="ProductCatalogInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22mb-4%20text-muted%22%7D" name="MagnifyingGlassIcon" size={64} className="mb-4 text-muted" />
                <h3 data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:584:16" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="584" data-component-end-line="586" data-component-file="ProductCatalogInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22mb-2%20font-heading%20text-xl%20font-semibold%20text-foreground%22%7D" className="mb-2 font-heading text-xl font-semibold text-foreground">
                  {t('common.noProductsFound')}
                </h3>
                <p data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:587:16" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="587" data-component-end-line="589" data-component-file="ProductCatalogInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mb-6%20text-center%20text-muted-foreground%22%7D" className="mb-6 text-center text-muted-foreground">
                  {t('common.adjustFilters')}
                </p>
                <button data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:590:16" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="590" data-component-end-line="594" data-component-file="ProductCatalogInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22btn-flat%20px-6%20py-3%20font-caption%20text-sm%20font-medium%20shadow-warm-sm%20transition-smooth%20hover-lift%20active-press%20focus-ring%22%7D"
                onClick={handleClearFilters}
                className="btn-flat px-6 py-3 font-caption text-sm font-medium shadow-warm-sm transition-smooth hover-lift active-press focus-ring">
                  {t('common.clearAllFilters')}
                </button>
              </div>
            }
          </div>
        </div>
      </main>

      {selectedProduct &&
      <VariantSelectorModal data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:602:6" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="602" data-component-end-line="612" data-component-file="ProductCatalogInteractive.tsx" data-component-name="VariantSelectorModal" data-component-content="%7B%22elementName%22%3A%22VariantSelectorModal%22%7D"
        productName={selectedProduct.name}
        productImage={selectedProduct.image}
        productAlt={selectedProduct.alt}
        variants={[
        { id: 'v1', name: '30ml', inStock: true },
        { id: 'v2', name: '50ml', inStock: true },
        { id: 'v3', name: '100ml', inStock: false }]
        }
        onClose={() => setSelectedProduct(null)}
        onConfirm={(variantId) => addToCart(selectedProduct.id, variantId)} />

      }

      {showSuccessMessage &&
      <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:617:6" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="617" data-component-end-line="624" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20bottom-6%20right-6%20z-%5B400%5D%20animate-slide-up%20rounded-md%20bg-success%20px-6%20py-4%20shadow-warm-lg%22%7D" className="fixed bottom-6 right-6 z-[400] animate-slide-up rounded-md bg-success px-6 py-4 shadow-warm-lg">
          <div data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:618:10" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="618" data-component-end-line="623" data-component-file="ProductCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%22%7D" className="flex items-center space-x-3">
            <Icon data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:619:12" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="619" data-component-end-line="619" data-component-file="ProductCatalogInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22text-success-foreground%22%7D" name="CheckCircleIcon" size={24} className="text-success-foreground" />
            <span data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:620:12" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="620" data-component-end-line="622" data-component-file="ProductCatalogInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-caption%20text-sm%20font-medium%20text-success-foreground%22%7D" className="font-caption text-sm font-medium text-success-foreground">
              {t('catalog.addedToCart')}
            </span>
          </div>
        </div>
      }

      <SimpleContentModal data-component-id="src/app/product-catalog/components/ProductCatalogInteractive.tsx:627:6" data-component-path="src/app/product-catalog/components/ProductCatalogInteractive.tsx" data-component-line="627" data-component-end-line="669" data-component-file="ProductCatalogInteractive.tsx" data-component-name="SimpleContentModal" data-component-content="%7B%22elementName%22%3A%22SimpleContentModal%22%7D"
        isOpen={isIframeModalOpen}
        onClose={() => setIsIframeModalOpen(false)}
        title={t('expert.modal.title')}
        sections={[
        { type: 'h1', content: t('expert.modal.intro') },
        { type: 'h2', content: t('expert.modal.paragraph1') },
        { type: 'paragraph', content: t('expert.modal.paragraph2') },
        { type: 'paragraph', content: t('expert.modal.paragraph3') },
        { type: 'paragraph', content: t('expert.modal.paragraph4') },
        { type: 'paragraph', content: t('expert.modal.paragraph5') },
        {
          type: 'toggle',
          toggleTitle: t('expert.modal.toggleTitle'),
          toggleItems: [
          {
            title: t('expert.modal.factor1.title'),
            content: t('expert.modal.factor1.content')
          },
          {
            title: t('expert.modal.factor2.title'),
            content: t('expert.modal.factor2.content')
          },
          {
            title: t('expert.modal.factor3.title'),
            content: t('expert.modal.factor3.content')
          },
          {
            title: t('expert.modal.factor4.title'),
            content: t('expert.modal.factor4.content')
          },
          {
            title: t('expert.modal.factor5.title'),
            content: t('expert.modal.factor5.content')
          }]

        },
        { type: 'paragraph', content: t('expert.modal.paragraph6') },
        { type: 'paragraph', content: t('expert.modal.paragraph7') },
        { type: 'paragraph', content: t('expert.modal.paragraph8') }]
        }
        buttonText={t('expert.modal.buttonText')}
        buttonUrl="https://celine-claire-cosmetic.fit/" />

    </>);


};

export default ProductCatalogInteractive;